/**
 * AdventureTitleImage Component
 * 
 * Displays an adventure title image with WebP/JPEG fallback support and lazy loading.
 * Used in adventure selection, lobby, and join screens.
 * Aspect ratio: 3:1 (wide landscape)
 */

import React, { useState } from 'react';
import { imageService } from '../../services/imageService';
import type { ImageVariantSize, ImageUrls } from '../../services/imageService';

export interface AdventureTitleImageProps {
  /**
   * Title image ID to display (e.g., 'fantasy', 'mystery', 'generic')
   * If undefined or not found, falls back to 'generic'
   */
  titleImageId?: string | undefined;
  
  /**
   * Size variant to display
   * - thumbnail: 768×256px (for smaller displays)
   * - card: 1536×512px (for adventure cards, selection)
   * - large: 2304×768px (for detail views)
   */
  variant?: ImageVariantSize;
  
  /**
   * Alt text for the image (for accessibility)
   */
  alt?: string;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Custom fallback element to display if image fails to load
   */
  fallback?: React.ReactNode;
  
  /**
   * Whether to use lazy loading (default: true)
   */
  lazy?: boolean;
  
  /**
   * If true, makes the image responsive (width: 100%, height: auto)
   * instead of using fixed size classes
   */
  responsive?: boolean;
}

/**
 * Default fallback component when title image cannot be loaded
 */
const DefaultFallback: React.FC<{ variant: ImageVariantSize; alt: string | undefined; responsive?: boolean }> = ({ variant, alt, responsive = false }) => {
  const sizes = responsive ? {
    thumbnail: 'w-full',
    card: 'w-full',
    large: 'w-full',
  } : {
    thumbnail: 'w-96 h-32',    // 768×256 equivalent (3:1)
    card: 'w-[48rem] h-64',     // 1536×512 equivalent (3:1)
    large: 'w-[72rem] h-96',    // 2304×768 equivalent (3:1)
  };
  
  const iconSizes = {
    thumbnail: 'text-4xl',
    card: 'text-8xl',
    large: 'text-9xl',
  };

  return (
    <div 
      className={`${sizes[variant]} flex items-center justify-center bg-gray-700 text-gray-400 overflow-hidden`}
      style={{
        borderRadius: '8px',
        border: '1px solid #374151',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
        ...(responsive ? { aspectRatio: '3 / 1' } : {})
      }}
      role="img"
      aria-label={alt || 'Adventure title image placeholder'}
    >
      <span className={iconSizes[variant]}>🗺️</span>
    </div>
  );
};

/**
 * AdventureTitleImage component
 */
export const AdventureTitleImage: React.FC<AdventureTitleImageProps> = ({
  titleImageId,
  variant = 'card',
  alt,
  className = '',
  fallback,
  lazy = true,
  responsive = false,
}) => {
  // Track which URL failed (not just a boolean)
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Effect to wait for image IDs to load
  React.useEffect(() => {
    let isMounted = true;
    
    if (!imageService.isLoaded()) {
      imageService.loadAvailableIds().then(() => {
        // IDs loaded successfully
        if (isMounted) {
          // Force a re-render by updating a state
          setIsLoading(false);
        }
      }).catch(() => {
        // Silently handle errors - images will fall back to placeholders
      });
    }
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Get title image URLs from the service
  const urls: ImageUrls = imageService.getTitleImageUrls(titleImageId, variant);

  // If THIS specific URL failed, show fallback
  if (failedUrl === urls.jpg) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <DefaultFallback variant={variant} alt={alt} responsive={responsive} />
    );
  }

  // Size classes for the container (3:1 aspect ratio)
  // If responsive mode is enabled, use flexible sizing
  const sizeClasses = responsive ? {
    thumbnail: 'w-full',
    card: 'w-full',
    large: 'w-full',
  } : {
    thumbnail: 'w-96 h-32',    // 768×256 equivalent
    card: 'w-[48rem] h-64',     // 1536×512 equivalent
    large: 'w-[72rem] h-96',    // 2304×768 equivalent
  };

  // Handle image load error
  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const failedSrc = e.currentTarget.src;
    console.warn(`Failed to load title image: ${titleImageId}`);
    setFailedUrl(failedSrc);
    setIsLoading(false);
  };

  // Handle image load success
  const handleLoad = () => {
    setIsLoading(false);
  };

  // Use URLs as key to force remount when they change
  const imageKey = `${urls.webp}-${urls.jpg}`;

  return (
    <div 
      className={`${sizeClasses[variant]} relative ${className}`}
      style={{
        borderRadius: '8px',
        border: '1px solid #374151',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        ...(responsive ? { aspectRatio: '3 / 1' } : {})
      }}
    >
      {/* Show placeholder while loading */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-700 animate-pulse">
          <span className="text-gray-500">Loading...</span>
        </div>
      )}
      
      {/* Picture element with WebP and JPEG sources */}
      {/* Using key prop to force remount when URLs change, clearing error state */}
      <picture key={imageKey} style={{ display: 'block', width: '100%', height: '100%' }}>
        {/* WebP source (modern browsers) */}
        <source
          type="image/webp"
          srcSet={urls.webp}
        />
        
        {/* JPEG fallback (all browsers) */}
        <img
          src={urls.jpg}
          alt={alt || 'Adventure title image'}
          loading={lazy ? 'lazy' : 'eager'}
          onError={handleError}
          onLoad={handleLoad}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
          className={`${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        />
      </picture>
    </div>
  );
};

export default AdventureTitleImage;

